import { Alert, Anchor, Button, Card, Center, Stack, Text, TextInput, Title } from "@mantine/core";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, api } from "@/api/client";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      // A genuine failure here (network, rate-limited) is fine to show
      // distinctly — it's "the request didn't go through," not "does this
      // account exist." The server's own response never distinguishes the
      // latter (§6.2), and this branch does not add a way to either.
      setError(
        err instanceof ApiError ? err.message : "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Center h="100vh" px="md">
      <Card withBorder shadow="sm" padding="xl" w={400} maw="100%">
        {submitted ? (
          <Stack>
            <Title order={2}>Check your email</Title>
            <Text size="sm" c="dimmed">
              If an account exists for {email}, a link to reset your password
              is on its way.
            </Text>
            <Anchor component={Link} to="/login" size="sm">
              Back to sign in
            </Anchor>
          </Stack>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stack>
              <div>
                <Title order={2}>Forgot your password?</Title>
                <Text size="sm" c="dimmed">
                  Enter your email and we'll send you a link to reset it.
                </Text>
              </div>

              {error && (
                <Alert color="red" variant="light">
                  {error}
                </Alert>
              )}

              <TextInput
                label="Email"
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />

              <Button type="submit" loading={submitting} fullWidth mt="sm">
                Send reset link
              </Button>

              <Text ta="center">
                <Anchor component={Link} to="/login" size="sm">
                  Back to sign in
                </Anchor>
              </Text>
            </Stack>
          </form>
        )}
      </Card>
    </Center>
  );
}
